/** Mediatip menu-only intent scheduling; never delays the actual navigation. */
export function createHeaderPrefetchIntent({
  prefetch,
  now = Date.now,
  schedule = (callback: () => void, delay: number) => setTimeout(callback, delay),
  cancel = (timer: ReturnType<typeof setTimeout>) => clearTimeout(timer),
}: {
  prefetch: (href: string) => void;
  now?: () => number;
  schedule?: (callback: () => void, delay: number) => ReturnType<typeof setTimeout>;
  cancel?: (timer: ReturnType<typeof setTimeout>) => void;
}) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let pendingHref: string | undefined;
  let lastStarted = -Infinity;
  const recent = new Map<string, number>();

  const clear = (href?: string) => {
    if (href && href !== pendingHref) return;
    if (timer !== undefined) cancel(timer);
    timer = undefined;
    pendingHref = undefined;
  };

  return {
    request(href: string) {
      clear();
      const previous = recent.get(href);
      if (previous !== undefined && now() - previous < 30_000) return;
      pendingHref = href;
      // Dwell, not a global immediate-start throttle. Crossing another row cancels it.
      timer = schedule(
        () => {
          timer = undefined;
          pendingHref = undefined;
          if (recent.size >= 32) recent.delete(recent.keys().next().value!);
          recent.set(href, now());
          lastStarted = now();
          try {
            prefetch(href);
          } catch {
            recent.delete(href);
          }
        },
        Math.max(300, 1000 - (now() - lastStarted)),
      );
    },
    cancel: clear,
  };
}
